/**
 * Contact Tags Service
 * CRUD operations for contact tags with autocomplete search
 */

import { supabase } from "./supabase";
import type {
  ContactTag,
  CreateContactTagInput,
} from "../types/contactTag.types";
import type { UUID } from "../types/common.types";

export async function getContactTags(contactId: UUID): Promise<ContactTag[]> {
  const { data, error } = await supabase
    .from("contact_tags")
    .select("*")
    .eq("contact_id", contactId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching contact tags:", error);
    throw new Error(`Failed to fetch contact tags: ${error.message}`);
  }

  return data as ContactTag[];
}

export async function addContactTag(
  input: CreateContactTagInput,
): Promise<ContactTag> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from("contact_tags")
    .insert([
      {
        ...input,
        created_by: user?.id,
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("Error adding contact tag:", error);
    throw new Error(`Failed to add contact tag: ${error.message}`);
  }

  return data as ContactTag;
}

/** Thrown when the delete reached no row: the tag is gone already, or (Regional / Territory Managers) it was added
 *  from an event outside the caller's team, which row-level security does not let them remove. */
export const TAG_NOT_REMOVED_MESSAGE =
  "This tag could not be removed. It may come from an event outside your team; ask an admin to remove it.";

export async function removeContactTag(
  contactId: UUID,
  tag: string,
): Promise<void> {
  // count the rows actually deleted: RLS filters a DELETE silently (0 rows, no error)
  const { error, count } = await supabase
    .from("contact_tags")
    .delete({ count: "exact" })
    .eq("contact_id", contactId)
    .eq("tag", tag);

  if (error) {
    console.error("Error removing contact tag:", error);
    throw new Error(`Failed to remove contact tag: ${error.message}`);
  }
  if (count === 0) {
    throw new Error(TAG_NOT_REMOVED_MESSAGE);
  }
}

export async function searchTags(query: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("contact_tags")
    .select("tag")
    .ilike("tag", `%${query}%`)
    .limit(20);

  if (error) {
    console.error("Error searching tags:", error);
    return [];
  }

  // Return unique tags
  const uniqueTags = [...new Set(data.map((d) => d.tag))];
  return uniqueTags;
}
